import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { auth } from '../../../firebase/config';
import Button from '../../../components/Button';
import Toast from '../../../components/Toast';
import { FiPlus, FiSearch, FiEye, FiEdit2 } from 'react-icons/fi';
import Icon from '../../../components/Icon';
import Table, { Column } from '../../../components/Table';
import LocationFormModal from '../components/LocationFormModal';
import {
    fetchRegionLocations,
    createRegionLocation,
    updateRegionLocation,
    type RegionLocation,
} from '../service/locationsService';

export default function Locations() {
    const [rows, setRows] = useState<RegionLocation[]>([]);
    const [filtered, setFiltered] = useState<RegionLocation[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [toast, setToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
        show: false,
        message: '',
        type: 'success',
    });

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<'add' | 'edit' | 'view'>('add');
    const [selected, setSelected] = useState<RegionLocation | null>(null);

    const uid = auth.currentUser?.uid;
    const userRole = sessionStorage.getItem('role');
    const isBankUser = userRole === 'bank';

    const [currentPage, setCurrentPage] = useState(1);
    const perPage = 20;

    const showToast = (message: string, type: 'success' | 'error' = 'success') => {
        setToast({ show: true, message, type });
    };

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const list = await fetchRegionLocations();
            setRows(list);
            setFiltered(list);
        } catch (e) {
            console.error(e);
            setToast({ show: true, message: 'Failed to load locations', type: 'error' });
        } finally {
            if (!silent) setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!uid) return;
        void load(false);
    }, [uid, load]);

    useEffect(() => {
        if (!searchTerm.trim()) {
            setFiltered(rows);
            setCurrentPage(1);
            return;
        }
        const t = searchTerm.toLowerCase();
        const next = rows.filter((r) => {
            const name = (r.name || '').toLowerCase();
            const addr = (r.headOfficeAddress || '').toLowerCase();
            return name.includes(t) || addr.includes(t);
        });
        setFiltered(next);
        setCurrentPage(1);
    }, [searchTerm, rows]);

    const totalPages = Math.ceil(filtered.length / perPage);
    const paginatedData = useMemo(
        () => filtered.slice((currentPage - 1) * perPage, currentPage * perPage),
        [filtered, currentPage]
    );

    const openAdd = () => {
        setSelected(null);
        setModalMode('add');
        setModalOpen(true);
    };

    const openView = (loc: RegionLocation) => {
        setSelected(loc);
        setModalMode('view');
        setModalOpen(true);
    };

    const openEdit = (loc: RegionLocation) => {
        setSelected(loc);
        setModalMode('edit');
        setModalOpen(true);
    };

    const handleSave = async (payload: {
        name: string;
        headOfficeAddress: string;
        latitude: string;
        longitude: string;
    }) => {
        if (modalMode === 'add') {
            await createRegionLocation(payload);
            showToast('Location added');
        } else if (modalMode === 'edit' && selected) {
            await updateRegionLocation(selected.id, payload);
            showToast('Location updated');
        }
        await load(true);
    };

    const columns: Column<RegionLocation>[] = [
        {
            header: 'Region name',
            accessor: 'name',
            width: '22%',
            render: (r) => <span className="font-medium text-gray-900 dark:text-gray-100">{r.name || '—'}</span>,
        },
        {
            header: 'Head office address',
            width: '38%',
            render: (r) => (
                <div
                    style={{
                        maxWidth: '320px',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                    }}
                    className="text-sm text-gray-600 dark:text-gray-400"
                >
                    {r.headOfficeAddress || '—'}
                </div>
            ),
        },
        {
            header: 'Coordinates',
            width: '20%',
            render: (r) => {
                const lat = r.location?.lat;
                const long = r.location?.long;
                if (lat == null || long == null) {
                    return <span className="text-gray-500 dark:text-gray-400">—</span>;
                }
                return (
                    <span className="tabular-nums text-sm text-gray-600 dark:text-gray-400">
                        {Number(lat).toFixed(5)}, {Number(long).toFixed(5)}
                    </span>
                );
            },
        },
        {
            header: 'Action',
            width: '12%',
            render: (r) => (
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => openView(r)}
                        className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400"
                        title="View"
                    >
                        <Icon icon={FiEye} className="h-4 w-4" />
                    </button>
                    {isBankUser && (
                        <button
                            type="button"
                            onClick={() => openEdit(r)}
                            className="inline-flex items-center justify-center rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium text-gray-700 transition-all hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400 dark:hover:bg-blue-900/20 dark:hover:text-blue-400"
                            title="Edit"
                        >
                            <Icon icon={FiEdit2} className="h-4 w-4" />
                        </button>
                    )}
                </div>
            ),
        },
    ];

    return (
        <>
            <div className="space-y-8">
                <div className="border-b border-gray-200 pb-6 dark:border-gray-800">
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Locations</h1>
                    <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
                        {isBankUser
                            ? 'Manage region locations used when creating branches: name, head office address, and map coordinates.'
                            : 'View region locations linked to branches.'}
                    </p>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="relative max-w-md flex-1">
                        <Icon
                            icon={FiSearch}
                            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                        />
                        <input
                            type="search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search by name or address…"
                            className="form-control w-full pl-9"
                        />
                    </div>
                </div>

                <Table
                    data={paginatedData}
                    columns={columns}
                    keyExtractor={(item) => item.id}
                    loading={loading}
                    emptyMessage={searchTerm ? 'No locations match your search' : 'No locations yet'}
                    pagination={{
                        currentPage,
                        totalPages: Math.max(1, totalPages),
                        onPageChange: setCurrentPage,
                    }}
                    headerActions={
                        isBankUser ? (
                            <Button type="button" onClick={openAdd} className="inline-flex items-center gap-2 !py-2">
                                <Icon icon={FiPlus} className="h-4 w-4" />
                                Add location
                            </Button>
                        ) : undefined
                    }
                />
            </div>

            <LocationFormModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                mode={modalMode}
                initial={selected}
                onSave={handleSave}
            />

            {toast.show && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast((s) => ({ ...s, show: false }))}
                />
            )}
        </>
    );
}
